import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserIcon, ChevronUpIcon, LogOut } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';

interface UserProfileProps {
  isCollapsed: boolean;
  /** Which way the dropdown menu opens. 'up' (default) suits a footer placement; 'down' suits top-of-sidebar placement. */
  direction?: 'up' | 'down';
}

const UserProfile: React.FC<UserProfileProps> = ({ isCollapsed, direction = 'up' }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();
  const { signOut } = useClerk();

  // Get user initials from name or email
  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.substring(0, 2).toUpperCase();
    }
    if (user?.primaryEmailAddress?.emailAddress) {
      return user.primaryEmailAddress.emailAddress.substring(0, 2).toUpperCase();
    }
    return 'GB';
  };

  const toggleMenu = () => {
    if (!isCollapsed) {
      setMenuOpen(!menuOpen);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close menu when sidebar collapses
  useEffect(() => {
    if (isCollapsed) {
      setMenuOpen(false);
    }
  }, [isCollapsed]);

  return (
    <div className="p-2 relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className={`w-full flex items-center transition-all duration-300 ease-out rounded-full hover:bg-white/5 cursor-pointer ${
          isCollapsed ? 'justify-center p-2' : 'px-3 py-2'
        }`}
        title={isCollapsed ? (user?.fullName || 'User Profile') : undefined}
      >
        {/* Avatar Circle */}
        {user?.imageUrl ? (
          <img
            src={user.imageUrl}
            alt={user.fullName || 'User'}
            className={`flex-shrink-0 rounded-full object-cover transition-all duration-300 ease-out ${
              isCollapsed ? 'w-10 h-10' : 'w-9 h-9'
            }`}
          />
        ) : (
          <div className={`flex-shrink-0 flex items-center justify-center bg-blue-500 text-white font-semibold rounded-full transition-all duration-300 ease-out ${
            isCollapsed ? 'w-10 h-10 text-sm' : 'w-9 h-9 text-xs'
          }`}>
            {getInitials()}
          </div>
        )}

        {/* User Info - Only visible when expanded */}
        <div className={`flex items-center justify-between transition-all duration-300 ease-out overflow-hidden ${
          isCollapsed
            ? 'flex-none ml-0 w-0 opacity-0'
            : 'flex-1 ml-3 w-auto opacity-100'
        }`}>
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-white whitespace-nowrap">
              {user?.fullName || 'User'}
            </span>
            <span className="text-xs text-gray-400 whitespace-nowrap truncate max-w-[140px]">
              {user?.primaryEmailAddress?.emailAddress || ''}
            </span>
          </div>

          {/* Chevron icon */}
          <ChevronUpIcon
            size={16}
            className={`text-gray-400 transition-transform duration-200 ${
              direction === 'up'
                ? menuOpen
                  ? 'rotate-180'
                  : ''
                : menuOpen
                  ? ''
                  : 'rotate-180'
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {menuOpen && !isCollapsed && (
        <div
          className={`absolute left-2 right-2 bg-[#1A2942] rounded-lg shadow-lg py-1 border border-white/10 z-20 ${
            direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <Link
            to="/profile"
            className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
            onClick={() => setMenuOpen(false)}
          >
            <UserIcon size={16} className="mr-2" />
            Your Profile
          </Link>
          <div className="border-t border-white/10 my-1"></div>
          <button
            onClick={() => {
              setMenuOpen(false);
              signOut();
            }}
            className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
          >
            <LogOut size={16} className="mr-2" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
